import { useDebounce } from "@uidotdev/usehooks";
import { BookOpenCheck, ChevronRight, Lock, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Link,
  type MetaFunction,
  type ShouldRevalidateFunctionArgs,
  useLoaderData,
  useSearchParams,
} from "react-router";

import { Badge } from "@/ui/badge";
import { Input } from "@/ui/input";

import { conceptSections, getConceptsBySection } from "./concepts/registry";
import { CourseFooter } from "./course-footer";
import { CourseShell } from "./course-shell";
import {
  LearningCatalog,
  LearningCatalogItem,
  LearningCatalogSectionHeader,
} from "./learning-catalog";
import { buildLearningHref } from "./navigation";
import type { ConceptsLoaderResponse } from "./types";

export const conceptsMeta: MetaFunction = () => {
  return [
    { title: `Concepts | ${publicEnv.siteTitle}` },
    {
      name: "description",
      content:
        "Focused references for GNU Make, recipe shells, managed tool installations, and the repository conventions around them.",
    },
  ];
};

// Keep client-side filtering from reloading access data for every query edit.
//
export function conceptsShouldRevalidate({
  currentUrl,
  defaultShouldRevalidate,
  formMethod,
  nextUrl,
}: ShouldRevalidateFunctionArgs) {
  const currentSearchParams = new URLSearchParams(currentUrl.search);
  const nextSearchParams = new URLSearchParams(nextUrl.search);
  const didQueryChange =
    currentSearchParams.get("q") !== nextSearchParams.get("q");

  currentSearchParams.delete("q");
  nextSearchParams.delete("q");

  const isQueryOnlyNavigation =
    !formMethod &&
    didQueryChange &&
    currentUrl.pathname === nextUrl.pathname &&
    currentSearchParams.toString() === nextSearchParams.toString();

  return isQueryOnlyNavigation ? false : defaultShouldRevalidate;
}

export function ConceptsPage() {
  const { canAccessPaidConcepts, dbUser } =
    useLoaderData<ConceptsLoaderResponse>();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get("q") ?? "";
  const [query, setQuery] = useState(urlQuery);
  const debouncedQuery = useDebounce(query, 300);
  const lastSynchronizedQueryRef = useRef(urlQuery);
  const conceptsSearchParams = new URLSearchParams(searchParams);
  if (query) {
    conceptsSearchParams.set("q", query);
  } else {
    conceptsSearchParams.delete("q");
  }
  const conceptsSearch = conceptsSearchParams.toString();
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleSections = conceptSections
    .map((section, sectionIndex) => {
      const concepts = getConceptsBySection(section.id)
        .map((concept, conceptIndex) => ({ concept, conceptIndex }))
        .filter(({ concept }) => {
          if (!normalizedQuery) {
            return true;
          }

          return [
            concept.title,
            concept.description,
            section.title,
            section.description,
          ].some((value) =>
            value.toLocaleLowerCase().includes(normalizedQuery),
          );
        });

      return { concepts, section, sectionIndex };
    })
    .filter(({ concepts }) => concepts.length > 0);
  const visibleConceptCount = visibleSections.reduce((count, section) => {
    return count + section.concepts.length;
  }, 0);

  // Reconcile same-route URL changes before synchronizing local search edits.
  //
  useEffect(() => {
    if (urlQuery !== lastSynchronizedQueryRef.current) {
      lastSynchronizedQueryRef.current = urlQuery;
      setQuery(urlQuery);
      return;
    }

    if (debouncedQuery === urlQuery) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    if (debouncedQuery) {
      nextSearchParams.set("q", debouncedQuery);
    } else {
      nextSearchParams.delete("q");
    }

    lastSynchronizedQueryRef.current = debouncedQuery;
    setSearchParams(nextSearchParams, { replace: true });
  }, [debouncedQuery, searchParams, setSearchParams, urlQuery]);

  return (
    <CourseShell dbUser={dbUser}>
      <main className="mx-auto flex w-[calc(100%-2rem)] max-w-5xl flex-col gap-10 pb-20 sm:w-[calc(100%-3rem)] sm:gap-14 sm:pb-28">
        <section className="mx-auto w-full pt-10 sm:pt-14 lg:pt-16">
          <div className="flex max-w-3xl flex-col gap-7">
            <Badge className="w-fit font-mono shadow-xs" variant="secondary">
              <BookOpenCheck />
              Concept guide
            </Badge>

            <div className="grid gap-4">
              <h1 className="max-w-3xl text-4xl leading-tight font-semibold text-balance sm:text-5xl">
                Makefile architecture concepts
              </h1>
              <p className="text-muted-foreground max-w-2xl text-lg leading-8 text-pretty">
                Explore Make and its surrounding shell, toolchain, and
                repository practices by subject instead of commit order. Each
                section begins with a free foundation, followed by deeper
                explanations included with full access.
              </p>
            </div>
          </div>
        </section>

        <section aria-labelledby="concept-list-title" className="grid gap-6">
          <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_20rem] md:items-end">
            <div className="grid max-w-2xl gap-2">
              <h2
                className="text-2xl leading-tight font-semibold"
                id="concept-list-title"
              >
                Concepts
              </h2>
              <p className="text-muted-foreground leading-7">
                Read a section in order, or open a concept exactly when a lesson
                references it.
              </p>
            </div>

            <div className="grid gap-2">
              <label
                className="text-muted-foreground text-xs font-medium"
                htmlFor="concept-search"
              >
                Find a concept
              </label>
              <div className="relative">
                <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input
                  className="pl-9"
                  id="concept-search"
                  placeholder="Search titles and descriptions"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
            </div>
          </div>

          <LearningCatalog
            label="Concept library"
            countLabel={`${visibleConceptCount} ${
              visibleConceptCount === 1 ? "concept" : "concepts"
            }`}
          >
            {visibleSections.map(({ concepts, section, sectionIndex }) => {
              const conceptsOrigin = `/concepts${
                conceptsSearch ? `?${conceptsSearch}` : ""
              }#${section.id}`;

              return (
                <section
                  key={section.id}
                  className="scroll-mt-20 border-t first:border-t-0"
                  id={section.id}
                >
                  <div className="bg-muted/20 px-4 py-4">
                    <LearningCatalogSectionHeader
                      description={section.description}
                      title={section.title}
                      eyebrow={`Section ${String(sectionIndex + 1).padStart(
                        2,
                        "0",
                      )}`}
                      meta={`${concepts.length} ${
                        concepts.length === 1 ? "concept" : "concepts"
                      }`}
                    />
                  </div>

                  <ol className="grid gap-1 p-2">
                    {concepts.map(({ concept, conceptIndex }) => {
                      const isLocked =
                        concept.access === "paid" && !canAccessPaidConcepts;
                      const conceptNumber = `${sectionIndex + 1}.${
                        conceptIndex + 1
                      }`;

                      return (
                        <LearningCatalogItem
                          key={concept.slug}
                          description={concept.description}
                          eyebrow={`Concept ${conceptNumber}`}
                          isMuted={isLocked}
                          marker={conceptNumber}
                          title={concept.title}
                          accessBadge={
                            <Badge
                              className="font-mono text-[10px]"
                              variant={
                                concept.access === "free"
                                  ? "secondary"
                                  : "outline"
                              }
                            >
                              {concept.access === "free" ? (
                                "Free"
                              ) : (
                                <>
                                  {isLocked && <Lock />}
                                  Full access
                                </>
                              )}
                            </Badge>
                          }
                          href={buildLearningHref({
                            destination: `/concepts/${concept.slug}`,
                            from: conceptsOrigin,
                          })}
                          trailing={
                            isLocked ? (
                              <Lock className="size-4" />
                            ) : (
                              <ChevronRight className="size-4" />
                            )
                          }
                        />
                      );
                    })}
                  </ol>
                </section>
              );
            })}

            {visibleConceptCount === 0 && (
              <p className="text-muted-foreground px-4 py-10 text-center text-sm">
                No concepts match “{query}”.
              </p>
            )}
          </LearningCatalog>

          <p className="text-muted-foreground text-sm">
            Not sure where Concepts fit?{" "}
            <Link
              className="underline-decoration text-foreground underline"
              to="/guide"
            >
              See how the learning views work
            </Link>
            .
          </p>
        </section>
      </main>

      <CourseFooter />
    </CourseShell>
  );
}
